// Rolling a streamed assistant message back to the start of a model round (PLT-8130).
//
// When a round's stream fails part-way (overload, rate limit, dropped connection, …) the chat
// backend runs the round again on a fresh stream. The failed attempt's text and parts are already on
// screen, so the server sends `round-start` before each round and `round-discard` before such a
// retry: the adapter marks its parts at the first and rolls back to the mark at the second, so the
// retry's output replaces the partial attempt instead of following it.
//
// This lives in its own module so it can be unit-tested without the adapter's module graph, which
// touches `localStorage` at load time (see `upload-error-message.ts`).
// ---------------------------------------------------------------------------

/** Where a round's output starts in the adapter's content parts. */
export interface RoundStartMark {
  partCount: number;
  /** The text part the round's first text delta appends to, or -1 when that delta starts a new part. */
  textIdx: number;
  /** The length of that text part when the round started. */
  textLength: number;
}

/** The adapter's content parts, told apart by `kind`; only `kind: 'text'` parts carry `text`. */
interface ContentPart {
  kind: string;
}

function textOf(part: ContentPart | undefined): string | undefined {
  return part?.kind === 'text' ? (part as { text?: string }).text : undefined;
}

export function markRoundStart(parts: readonly ContentPart[], currentTextIdx: number): RoundStartMark {
  const text = currentTextIdx >= 0 ? textOf(parts[currentTextIdx]) : undefined;
  return {
    partCount: parts.length,
    textIdx: text !== undefined ? currentTextIdx : -1,
    textLength: text?.length ?? 0,
  };
}

/**
 * The content parts as they were at `mark`, and the text part the next text delta appends to. Text
 * parts are appended to in place, so the one open at the mark is cut back to its length then.
 */
export function rollBackToRoundStart<P extends ContentPart>(
  parts: readonly P[],
  mark: RoundStartMark,
): { parts: P[]; currentTextIdx: number } {
  const kept = parts.slice(0, mark.partCount);
  const textPart = mark.textIdx >= 0 ? kept[mark.textIdx] : undefined;
  const text = textOf(textPart);
  if (textPart && text !== undefined) {
    kept[mark.textIdx] = { ...textPart, text: text.slice(0, mark.textLength) };
  }
  return { parts: kept, currentTextIdx: mark.textIdx };
}
